/**
 * 版本更新记录弹窗组件
 */

import React from 'react';
import { Modal, Timeline, Tag, Spin, Empty, Typography } from 'antd';
import {
  BulbOutlined,
  BugOutlined,
  RiseOutlined,
  WarningOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useChangelog } from '@/hooks/useChangelog';
import type { ChangeType, ChangelogEntry } from '@/types/changelog';
import './index.less';

const { Text, Title } = Typography;

export interface ChangelogModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * 获取更新类型图标
 */
const getChangeTypeIcon = (type: ChangeType) => {
  const iconMap: Record<ChangeType, React.ReactNode> = {
    feature: <BulbOutlined style={{ color: '#52c41a' }} />,
    fix: <BugOutlined style={{ color: '#faad14' }} />,
    improvement: <RiseOutlined style={{ color: '#1890ff' }} />,
    breaking: <WarningOutlined style={{ color: '#ff4d4f' }} />,
  };
  return iconMap[type] || <BulbOutlined />;
};

/**
 * 获取更新类型标签颜色
 */
const getChangeTypeColor = (type: ChangeType): string => {
  const colorMap: Record<ChangeType, string> = {
    feature: 'success',
    fix: 'warning',
    improvement: 'processing',
    breaking: 'error',
  };
  return colorMap[type] || 'default';
};

/**
 * 单个版本记录项
 */
const ChangelogItem: React.FC<{ entry: ChangelogEntry }> = ({ entry }) => {
  const { t } = useTranslation();

  // 按类型分组
  const groupedChanges = entry.changes.reduce((acc, change) => {
    if (!acc[change.type]) {
      acc[change.type] = [];
    }
    acc[change.type].push(change.description);
    return acc;
  }, {} as Record<ChangeType, string[]>);

  const typeOrder: ChangeType[] = ['feature', 'improvement', 'fix', 'breaking'];

  return (
    <div className="changelog-item">
      <div className="changelog-item-header">
        <Tag color="blue" className="version-tag">v{entry.version}</Tag>
        <Text type="secondary" className="date">
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {entry.date}
        </Text>
      </div>
      <div className="changelog-item-content">
        {typeOrder.map((type) => {
          const changes = groupedChanges[type];
          if (!changes?.length) return null;

          return (
            <div key={type} className="change-group">
              <div className="change-group-title">
                {getChangeTypeIcon(type)}
                <Tag color={getChangeTypeColor(type)} style={{ marginLeft: 8 }}>
                  {t(`changelog.type.${type}`)}
                </Tag>
              </div>
              <ul className="change-list">
                {changes.map((desc, index) => (
                  <li key={index}>{desc}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ChangelogModal: React.FC<ChangelogModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { changelog, loading, error } = useChangelog();

  const renderContent = () => {
    if (loading) {
      return (
        <div className="changelog-loading">
          <Spin tip={t('common.loading')}><div style={{ minHeight: 100 }} /></Spin>
        </div>
      );
    }

    if (error) {
      return (
        <Empty
          description={t('changelog.loadError')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    if (!changelog?.versions?.length) {
      return (
        <Empty
          description={t('changelog.noData')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <Timeline
        className="changelog-timeline"
        items={changelog.versions.map((entry) => ({
          key: entry.version,
          children: <ChangelogItem entry={entry} />,
        }))}
      />
    );
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {t('changelog.title')}
        </Title>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      className="changelog-modal"
      styles={{
        body: {
          maxHeight: '60vh',
          overflowY: 'auto',
        },
      }}
    >
      {renderContent()}
    </Modal>
  );
};

export default ChangelogModal;
